'use client'
import type { ReactNode } from 'react'

import { faArrowRightLong } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import {
  lineClamp,
  sendGTMEvent,
} from '@b-stock/fe-core/bstock-next/components'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  Duration,
  FormattedMessage,
  Tag,
  TagType,
} from '@b-stock/fe-core/bstock-react'
import { Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'
import type { AllListingsListing } from '@b-stock/search-api-client'

import { getActivePhase } from '@/buyer-portal/helpers/auctions/buyingFormat'
import Image from '@/components/Image'
import { getLogoOverride } from '@/components/listings/listingCard/marketplaceLogoConfig'
import { PERSONALIZED_CATEGORIES_CAROUSEL_NAME } from '@/components/personalizedCategoryCarousel/constants'
import ComponentErrorBoundary from '@/helpers/ComponentErrorBoundary'
import { parseCustomDate } from '@/helpers/dateUtils'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { allListingsToGTMItemInput } from '@/helpers/telemetry/gtmItemAdapters'
import { useItemListContext } from '@/helpers/telemetry/itemListContext'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { transformListingToGTMItem } from '@/helpers/telemetry/transformListingToGTMItem'

interface IProps {
  listing: AllListingsListing
  carousel_name?: string | null
  carousel_position?: number
  screenName?: string
  position: number
  /**
   * Optional extra line rendered under "Closes in", for carousels that need
   * one more fact on the card (e.g. Better Odds' unique-bidder count).
   */
  extra?: ReactNode
  /** Marks the listing's seller as new, the same tag the ALP card shows. */
  isNewSeller?: boolean
}

interface AuctionLinkProps {
  title?: string
  auction_id?: string
  item_type?: string
  item_marketplace?: string
  item_brand?: string
  item_category?: string
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 12.375rem;
  max-width: 12.375rem;
  height: 21.25rem;
  max-height: 21.25rem;
  padding: 0;
  flex-shrink: 0;
`

const AuctionLink = styled.a<AuctionLinkProps>`
  position: relative;
  display: block;
  height: 100%;

  &:before {
    position: absolute;
    top: -0.125rem;
    right: -0.125rem;
    bottom: -0.125rem;
    left: -0.125rem;
    content: '';
    border-radius: 0.375rem;
    border: 0.125rem solid transparent;
    transition: border-color 0.3s ease;
    pointer-events: none;
  }

  &:hover:before {
    border-color: ${designColors.primary.brightBlue};
  }
`

const LogoBlock = styled.div`
  display: flex;
  height: 3.125rem;
  max-height: 3.125rem;
  padding: 0 1.0625rem;
  border: 0.0625rem solid ${designColors.neutral.mediumGray};
  border-bottom: none;
  border-radius: 0.3125rem 0.3125rem 0 0;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
`
const LogoImage = styled(Image)`
  padding: 0.3125rem 0;
  object-fit: contain;
`

const VectorLogoImage = styled.img`
  padding: 0.3125rem 0;
  object-fit: contain;
  width: 100%;
  height: 100%;
`

const AuctionImageHolder = styled.div`
  position: relative;
  display: flex;
  height: 8.75rem;
  max-height: 8.75rem;
  border: 0.0625rem solid ${designColors.neutral.mediumGray};
  justify-content: center;
  align-items: center;
`

const AuctionImage = styled(Image)`
  width: 7.1875rem;
  height: 7.1875rem;
  max-width: 95%;
  max-height: 95%;
  object-fit: contain;
`

const NewSellerTag = styled(Tag)`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  white-space: nowrap;
  line-height: 1rem;
`

const LotInfo = styled.div`
  min-height: 9.375rem;
  max-height: 9.375rem;
  padding: 1.375rem 0.9375rem 0.9375rem;
  border: 0.0625rem solid ${designColors.neutral.mediumGray};
  border-top: none;
  border-radius: 0 0 0.3125rem 0.3125rem;
  text-align: left;
  font-size: 0.875rem;
`
const ArrowIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 0.9375rem;
  bottom: 0.625rem;
  font-size: 1.25rem;
  width: 1.25rem;
  max-width: 1.25rem;
  color: ${designColors.primary.brightBlue};
`
const Title = styled.p`
  ${Typography.Body3}
  margin-bottom: 0.6875rem;
  line-height: 1.0625rem;
  ${lineClamp(3)}
`

const Bids = styled.p`
  ${Typography.Body3}
  line-height: 1.0625rem;
`
const Closes = styled(Bids)``
const Extra = styled(Bids)``

const AuctionCard = ({
  listing,
  carousel_name,
  carousel_position,
  screenName,
  position,
  extra,
  isNewSeller = false,
}: IProps) => {
  //For Amazon US and UK auctions ONLY
  const vectorLogoUrl = listing.siteVectorLogoUrl || undefined
  const logoUrl =
    getLogoOverride(listing.siteAbb) ?? vectorLogoUrl ?? listing.siteLogoUrl
  // Docserv URLs require auth — Next.js Image fetches server-side without
  // cookies, causing 401s. Treat them as vector logos so the browser fetches
  // them directly with session credentials.
  const usePlainImg =
    logoUrl === vectorLogoUrl || (!!logoUrl && logoUrl.includes('docserv'))

  const endTime = parseCustomDate(listing.endTime)
  const isBuyNowPhase =
    getActivePhase({
      listing: { auction3MP: null, auctionEnt: null, ...listing },
      enableFirstDibs: false,
    }) === 'BINO'
  const { trackButtonClicked, ...analytics } = useAnalytics()
  const { itemListId, itemListName } = useItemListContext()
  const { formatMessage } = useIntl()
  const ldClient = useLDClient()

  // Get impression ID from nested analytics data
  const impressionId =
    'listing_card_impression_id' in analytics
      ? String(analytics.listing_card_impression_id)
      : null

  const sortType = 'sort_type' in analytics ? String(analytics.sort_type) : null

  const handleClickAuctionCard = () => {
    // numberOfBids is typed non-null but has been seen absent at runtime (the
    // display guards it too), so normalize a missing value to null for analytics.
    const bidsExisting =
      typeof listing.numberOfBids === 'number' ? listing.numberOfBids : null
    // Segment analytics event and metadata transcribed from old wordpress code
    // TODO: Update entity_type to based on the role of the user once we
    // implement user authentication.
    trackButtonClicked({
      screenName: screenName ?? 'homepage',
      buttonName: 'view_details',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      auction_id: listing.auctionId,
      // TODO: there does not appear to be an analog for these fields in the ai-search endpoint
      // listing_source: auction.inventoryType,
      // seller_name_temp: auction.inventoryType,
      seller_name_ua: listing.storefrontName,
      carousel_name: carousel_name,
      carousel_position: carousel_position,
      url: document.URL,
      referrer: document.referrer,
      cta_url: listing.auctionUrl,
      listing_position: position,
      bids_existing: bidsExisting,
      is_new_seller: isNewSeller,
      id: listing.id,
      recommended_rank: listing.customRank,
      is_control: listing.randomSetLabel,
      fmv_rank: listing.percentFMV ?? null,
      ...(impressionId && { listing_card_impression_id: impressionId }),
      ...(sortType && { sort_type: sortType }),
    })
    if (carousel_name === PERSONALIZED_CATEGORIES_CAROUSEL_NAME) {
      // Separate event stream from the Segment button_click above — this one
      // feeds the ranking-rationale-label LD experiment's click-occurrence
      // metric, which is LaunchDarkly-hosted and keyed on this exact event name.
      ldClient?.track('button_click')
    }
    const item = transformListingToGTMItem(
      allListingsToGTMItemInput(listing),
      position,
      itemListId,
      itemListName
    )
    sendGTMEvent({ ecommerce: null })
    sendGTMEvent({
      event: 'select_item',
      platform: GTM_PLATFORM,
      ecommerce: {
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: [item],
      },
    })
  }

  return (
    <ComponentErrorBoundary>
      <Wrapper data-gtm-ignore>
        <AuctionLink
          href={listing.auctionUrl}
          title={listing.title}
          target="_blank"
          rel="noopener"
          onClick={handleClickAuctionCard}
        >
          {!!listing.storefrontName && (
            <LogoBlock>
              {logoUrl ? (
                usePlainImg ? (
                  <VectorLogoImage
                    data-testid="storefront-logo"
                    src={logoUrl}
                    alt={listing.storefrontName}
                  />
                ) : (
                  <LogoImage
                    data-testid="storefront-logo"
                    src={logoUrl}
                    alt={listing.storefrontName}
                    fill
                    sizes="85%"
                    priority
                  />
                )
              ) : (
                <b data-testid="storefront-name">{listing.storefrontName}</b>
              )}
            </LogoBlock>
          )}

          <AuctionImageHolder>
            {isNewSeller && (
              <NewSellerTag type={TagType.info} data-testid="new-seller-tag">
                <FormattedMessage id="Auction.tag.newSeller" />
              </NewSellerTag>
            )}
            <AuctionImage
              src={
                listing.primaryImageUrl ||
                '/home-portal/images/fallbackListingImage.png'
              }
              alt={formatMessage(
                { id: 'Auction.image.alt' },
                { title: listing.title }
              )}
              width={115}
              height={115}
              priority
            />
          </AuctionImageHolder>
          <LotInfo>
            <Title data-testid="listing-title">{listing.title}</Title>
            {typeof listing.numberOfBids === 'number' && !isBuyNowPhase && (
              <Bids data-testid="number-of-bids">
                {formatMessage({ id: 'Auction.status.bids' })}{' '}
                <strong>{listing.numberOfBids}</strong>
              </Bids>
            )}
            <Closes data-testid="closes-in">
              {formatMessage({ id: 'Auction.status.ClosesIn' })}{' '}
              <strong>
                <Duration target={endTime} format="short" />
              </strong>
            </Closes>
            {extra != null && <Extra data-testid="card-extra">{extra}</Extra>}
            <ArrowIcon icon={faArrowRightLong} />
          </LotInfo>
        </AuctionLink>
      </Wrapper>
    </ComponentErrorBoundary>
  )
}

export default AuctionCard
